import { useEffect, useRef, useState } from "react";

import { useEyeStore } from "../../Store/useEyeReport";
import { fetchReportData } from "../../services/network";
import Phi from "../../Phi/Phi";

import { blueEyes } from "../../Assets/images";

import StudentCard from "../../Components/UI/StudentCard";
import ComplaintsCard from "../../Components/UI/ComplaintsCard";
import MultilineTextBox from "../../Components/UI/MultilineTextBox";

import Precaution from "../../Components/UI/Precaution";
import ReportFooter from "../../Components/UI/ReportFooter";
import SectionHeading from "../../Components/UI/SectionHeading";
import SuggestedInvestigationCard from "../../Components/UI/SuggestedInvestigationCard";

import OptometryExamination from "../../Components/report/eyeReport/OptometryExamination";

import SimpleLoader from "../../Components/Loaders/SimpleLoader";

import {
  DentalIconSvg,
  EditSvg,
  EyeIconSvg,
  GirlIconSvg,
  SearchSuggestionIconSvg,
  UserIconSvg,
} from "../../utils/Svgs";

import {
  ReportRXData,
  reportStrings,
} from "../../utils/helpers/newReportHelper";

import { PALLETTE } from "../../utils/theme";
import { V2ReportStyle } from "../../styles/V2ReportStyle";
import ScreenShotWrapper from "../../Components/report/ScreenShotWrapper";
import Page from "./Page";
import { useUserStore } from "../../Store/userStore";
import { generateReportLogic } from "../../services/reportService";
import ReportHeader from "../../Components/report/ReportHeader";
import AssessmentNotDoneWrapper from "../../Components/report/AssessmentNotDoneWrapper";

const EyeReportScreen = ({ location }: { location: any }) => {
  const [assessmentError, setAssessmentError] = useState(null);
  const { handleMspLogo, mspLogo } = useUserStore((state) => state);
  const eyeReport = useEyeStore((state) => state);

  const sessionData = sessionStorage.getItem("report_navigation_state");
  const storedParams = sessionData ? JSON.parse(sessionData) : {};

  // Reads location.state if navigated internally, falls back to sessionStorage for new tab
  const params = location?.state || storedParams;
  const [isLoading, setLoading] = useState(false);

  const pdfWrapperRef = useRef<any>(null);

  const studentId = params?.user_id || params?.student_id;
  const eventTrackId = params?.eventTrackId;
  const activityId = params?.activityId;

  const jwt = Phi.getSession()?.jwt;

  const getchData = async (activity_id: number, assessmentId: number) => {
    try {
      setLoading(true);

      const res = await fetchReportData({
        activity_id,
        student_id: studentId,
        jwt,
        event_track_id: assessmentId,
      });
      console.log("res", JSON.stringify(res, null, 2));

      if (res?.results?.length > 0) {
        const parsedAssessment = JSON.parse(res.results[0].assessmentdata);

        if (parsedAssessment?.data?.Eye_Incomplete_Remark == "105") {
          setAssessmentError(true);
        }

        eyeReport.setEyeReportData(parsedAssessment?.data);
        await generateReportLogic({
          jwt: jwt,
          studentId: studentId,
          handleMspLogo,
        });
      }
    } catch (error) {
      console.error("error getting eye report", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getchData(activityId, eventTrackId);
  }, []);

  return (
    <div
      style={{
        ...V2ReportStyle.mainContainer,
        backgroundColor: PALLETTE.white,
        minHeight: "100vh",
      }}
    >
      {isLoading ? (
        <SimpleLoader />
      ) : (
        <>
          {/* Header */}

          <ReportHeader title="Eye Report" pdfWrapperRef={pdfWrapperRef} />

          <ScreenShotWrapper
            ref={pdfWrapperRef}
            studentName={params?.name || params?.studentname}
            reportType="Eye"
          >
            {(targetRef: any) => (
              <div
                ref={targetRef}
                style={{
                  padding: "3rem",
                  backgroundColor: PALLETTE.background,
                }}
              >
                {assessmentError ? (
                  <AssessmentNotDoneWrapper>
                    <StudentCard
                      name={params?.name || params?.studentname}
                      schoolName={params?.class}
                      icon={
                        params?.gender === "FEMALE" || params?.gender == "F" ? (
                          <GirlIconSvg />
                        ) : (
                          <UserIconSvg />
                        )
                      }
                    />
                  </AssessmentNotDoneWrapper>
                ) : (
                  <Page>
                    <StudentCard
                      name={params?.name || params?.studentname}
                      schoolName={params?.class}
                      icon={
                        params?.gender === "FEMALE" || params?.gender == "F" ? (
                          <GirlIconSvg />
                        ) : (
                          <UserIconSvg />
                        )
                      }
                    />

                    {/* Optometry Examination */}

                    <OptometryExamination />

                    {/* Clinical Examination */}

                    <SectionHeading
                      icon={<DentalIconSvg />}
                      title="Clinical Examination"
                    />

                    <ComplaintsCard
                      label={
                        eyeReport.NeedAttention?.leftEye
                          ? "Need Attention"
                          : "Normal"
                      }
                      remark={eyeReport.EyesOtherData?.Clinical_Left_Eye_Others}
                      data={
                        eyeReport.eyeReportData?.clinicalExamination?.leftEye
                      }
                      imageSource={blueEyes}
                      title="Left Eye"
                      topLefticon={<EyeIconSvg />}
                      suggestionText={
                        eyeReport.NeedAttention?.leftEye
                          ? reportStrings.leftEye
                          : ""
                      }
                    />

                    <ComplaintsCard
                      label={
                        eyeReport.NeedAttention?.rightEye
                          ? "Need Attention"
                          : "Normal"
                      }
                      remark={
                        eyeReport.EyesOtherData?.Clinical_Right_Eye_Others
                      }
                      data={
                        eyeReport.eyeReportData?.clinicalExamination?.rightEye
                      }
                      imageSource={blueEyes}
                      title="Right Eye"
                      topLefticon={<EyeIconSvg />}
                      suggestionText={
                        eyeReport.NeedAttention?.rightEye
                          ? reportStrings.rightEye
                          : ""
                      }
                    />

                    {/* Remarks */}

                    {eyeReport.EyeMetaData?.Eye_Remarks?.length > 0 && (
                      <div style={V2ReportStyle.card}>
                        <MultilineTextBox
                          description={eyeReport.EyeMetaData?.Eye_Remarks}
                          label="Remarks"
                          icon={<EditSvg />}
                        />
                      </div>
                    )}

                    {/* Suggested Investigations */}

                    <SuggestedInvestigationCard
                      sections={[
                        "Eye_Doctor_Consultation_",
                        "Refraction_",
                        "Spectacles_",
                      ]}
                      label={
                        eyeReport.NeedAttention?.eyeSuggestedInvestigation
                          ? "Need Attention"
                          : "Normal"
                      }
                      remark={eyeReport.EyesOtherData?.Eye_Other_Tests}
                      data={eyeReport.EyeSuggestedInvestigationData}
                      title="Suggested Investigations"
                      topLefticon={<SearchSuggestionIconSvg />}
                    />

                    {/* Precautions */}

                    <Precaution data={ReportRXData.eye} />

                    {/* Footer */}

                    <ReportFooter
                      doctorName={eyeReport.EyeMetaData?.Eye_Doctor_No}
                      designation={
                        eyeReport.EyeMetaData?.Eye_Doctor_Designation
                      }
                      docSignature={eyeReport.EyeMetaData?.Eye_Doctor_Path}
                      hospitalName={mspLogo[eventTrackId]}
                    />
                  </Page>
                )}
              </div>
            )}
          </ScreenShotWrapper>
        </>
      )}
    </div>
  );
};

export default EyeReportScreen;
